import * as H from 'history'
import  React from 'react'
import { Alert } from 'react-bootstrap'
import { ClaimController } from '../../../../controllers/ClaimController'
import { ClaimModel } from '../../../../models/ClaimModel'
import { MarketShare } from '../../../../models/MarketShare'
import { Option } from '../../../../models/Option'
import { ISupportSpinner } from '../../../../shared/Ajax'
import { Backdrop } from '../../../../shared/Backdrop'
import { Table } from '../../../../shared/table'
import { SaveCancelButtons } from '../../controls/SaveCancelButtons'
import { calculateEarnings, calculateExpenses } from '../../GrantEstimation'
import {
  marketShareColumns, MarketShareDetails, marketShareSummaryRows, calculateAllocations,
} from './MarketShareDetails'

export interface MarketSharePageProps {
    history: H.History
    model: ClaimModel
    onChange: { (changes: Partial<ClaimModel>, callback?: { (): any }): any }
    markets: Option<string>[]
}

export interface MarketSharePageState {
    loading: boolean
    marketShare: MarketShare[]
}

export class MarketSharePage extends React.Component< MarketSharePageProps, MarketSharePageState> implements ISupportSpinner {
    ajax: ClaimController
    table: Table<MarketShare>
    allocationError: HTMLDivElement

    expenseTotal: number;
    earningsTotal: number;

    constructor(props: MarketSharePageProps) {
      super(props)

      this.ajax = new ClaimController()

      this.expenseTotal = calculateExpenses(this.props.model).total
      this.earningsTotal = calculateEarnings(this.props.model).total

      this.state = {
        marketShare: props.model.marketShare ? props.model.marketShare.map((r) => r) : [],
        loading: false,
      }
    }

    componentWillUnmount() {
      this.ajax.cancel()
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
      this.setState({ loading: on }, callback)
    }

    areAllocationsValid(): boolean {
      const allocations = calculateAllocations(this.state.marketShare)
      if (allocations.allocatedExpenses > this.expenseTotal || Math.abs(allocations.allocatedEarnings) > Math.abs(this.earningsTotal)) { return false }

      return true
    }

    save = () => {
      this.table.saveAndClose((res) => {
        if (!this.areAllocationsValid()) {
          this.allocationError.focus()
          return
        }

        if (res) {
          this.props.onChange({ marketShare: this.state.marketShare }, () => {
            this.ajax.updateMarketShare(this, this.props.model, () => {
              this.props.history.goBack()
            })
          })
        }
      })
    }

    render() {
      const columns = [...marketShareColumns(this.props.markets, this.expenseTotal, this.earningsTotal, this.props.model.claimYear)]

      return (
        <>
          <SaveCancelButtons onCancel={this.props.history.goBack} onSave={this.save} />
          <h2 className="mt-3">Summary of Expenses and Earnings</h2>
          <p className="mt-3 mb-3">Please provide an estimate of the expenses and exports by country</p>
          <ul>
            <li>Details of up to 6 countries can be provided.</li>
            <li>
              Only include countries that have 10% or more of exports or expenses in this application.
              <br />
              All other amounts should be included in "Rest of the world"
            </li>
            <li>The "Rest of the world" field is automatically calculated so that the total amount equals the amount claimed in the application form schedules</li>
            <li>There are 2 options to complete this section. The estimates can be provided by A$ or %</li>
          </ul>
          <p>
            Note: Expenses and export earnings related to the Democratic Peoples's Republic of Korea (North Korea) or New Zealand or Iran (up to and including 17 January 2016) are not eligible.
          </p>

          <div tabIndex={-1} ref={(r) => this.allocationError = r} className="mt-3" style={{ display: !this.areAllocationsValid() ? '' : 'none' }}>
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              The sum of allocated expenses and earnings cannot exceed the total expenses and earnings respectively.
            </Alert>
          </div>

          <Table<MarketShare>
            id="table"
            label="Total of Expenses and Earnings"
            ref={(r) => this.table = r}
            value={this.state.marketShare}
            summaryRows={marketShareSummaryRows(this.state.marketShare, this.expenseTotal, this.earningsTotal)}
            onChange={(marketShare) => this.setState({ marketShare })}
            columns={columns}
            renderSection={(p, i) => <MarketShareDetails {...p} marketsAlreadyAllocated={this.state.marketShare} markets={this.props.markets} claimYear={this.props.model.claimYear} expenseTotal={this.expenseTotal} earningTotal={this.earningsTotal} />}
            match={(a, b) => a.id == b.id}
            allowEdit
            alwaysShowAllRows
            maxRows={6}
          />

          <SaveCancelButtons onCancel={this.props.history.goBack} onSave={this.save} />

          <Backdrop on={this.state.loading} />
        </>
      )
    }
}
